import { Button } from "@/components/ui/button";
import { Loader2, ArrowLeft, Save, Users, Zap, Send, TrendingUp, Sparkles, CheckCircle2 } from "lucide-react";
import { CampaignStatusBadge, StepChips } from "./campaignModel";

const STATS = [
  { label: "Prospects", field: "totalProspects", icon: Users, color: "text-white" },
  { label: "Enriched", field: "enrichedCount", icon: Zap, color: "text-ism-teal" },
  { label: "Emails Sent", field: "sentCount", icon: Send, color: "text-amber-400" },
  { label: "Replies", field: "repliedCount", icon: TrendingUp, color: "text-green-400" },
];

const progressText = (c) =>
  c?.status === "enriching"
    ? `Enriching... ${c?.enrichedCount || 0}/${c?.totalProspects}`
    : `Generating cadences... ${c?.sequencedCount || 0}/${c?.enrichedCount || 0}`;

const progressPct = (c) =>
  c?.status === "enriching"
    ? ((c?.enrichedCount || 0) / Math.max(c?.totalProspects, 1)) * 100
    : ((c?.sequencedCount || 0) / Math.max(c?.enrichedCount || 1, 1)) * 100;

export const DetailHeader = ({ campaign: c, processing, onBack, onDelete, onSaveTemplate }) => (
  <div className="flex items-center justify-between gap-4 flex-wrap">
    <div className="flex items-center gap-3 min-w-0">
      <Button onClick={onBack} variant="ghost" size="sm" className="text-gray-400 hover:text-white" data-testid="back-to-campaigns">
        <ArrowLeft className="h-4 w-4 mr-1" /> Back
      </Button>
      <div className="min-w-0">
        <h1 className="text-xl text-white font-semibold flex items-center gap-2 flex-wrap">
          {c?.name || "Campaign"}
          {c && <CampaignStatusBadge status={c.status} />}
        </h1>
        {c && <p className="text-xs text-gray-500 truncate">{c.totalProspects} prospects — {c.sourceFile}</p>}
      </div>
    </div>
    <div className="flex items-center gap-2">
      {processing && (
        <div className="flex items-center gap-2 text-amber-400 text-sm mr-4" data-testid="campaign-progress-text">
          <Loader2 className="h-4 w-4 animate-spin" />
          {progressText(c)}
        </div>
      )}
      {!processing && c?.cadenceConfig?.stepLabels?.length > 0 && (
        <Button onClick={onSaveTemplate} variant="ghost" size="sm" className="text-ism-teal hover:text-ism-teal hover:bg-ism-teal/10 text-xs" data-testid="save-as-template-btn">
          <Save className="h-3.5 w-3.5 mr-1" /> Save as Template
        </Button>
      )}
      <Button onClick={onDelete} variant="ghost" size="sm" className="text-red-400/60 hover:text-red-400 text-xs" data-testid="delete-campaign-btn">
        Delete
      </Button>
    </div>
  </div>
);

export const StatsRow = ({ campaign: c }) => (
  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
    {STATS.map((stat) => (
      <div key={stat.label} className="glass-card p-4 text-center" data-testid={`stat-${stat.label.toLowerCase().replace(" ", "-")}`}>
        <stat.icon className={`h-5 w-5 mx-auto mb-1 ${stat.color}`} />
        <p className={`text-2xl font-bold ${stat.color}`}>{c?.[stat.field] || 0}</p>
        <p className="text-xs text-gray-500">{stat.label}</p>
      </div>
    ))}
  </div>
);

export const CadenceSummary = ({ campaign: c }) => {
  const cfg = c.cadenceConfig;
  return (
    <div className="glass-card p-4 border-l-4 border-ism-teal/40" data-testid="cadence-summary">
      <div className="flex items-start gap-3">
        <Sparkles className="h-5 w-5 text-ism-teal flex-shrink-0 mt-0.5" />
        <div className="flex-1 space-y-2">
          <p className="text-sm text-white">{cfg.steps}-step {c.cadenceStyle || "consultative"} cadence</p>
          {cfg.reasoning && <p className="text-xs text-gray-500">{cfg.reasoning}</p>}
          <StepChips cadenceConfig={cfg} />
        </div>
      </div>
    </div>
  );
};

export const ProcessingState = ({ campaign: c }) => (
  <div className="glass-card p-6 text-center" data-testid="campaign-processing">
    <Loader2 className="h-8 w-8 animate-spin text-ism-teal mx-auto mb-3" />
    <p className="text-white font-medium">
      {c?.status === "enriching" ? "Enriching your prospects with web intelligence..." : "Crafting personalized email cadences..."}
    </p>
    <p className="text-xs text-gray-500 mt-1">This takes about 10-20 seconds per prospect. You can leave and come back.</p>
    <div className="mt-4 w-full bg-white/5 rounded-full h-2">
      <div className="bg-ism-teal h-2 rounded-full transition-all duration-500" style={{ width: `${progressPct(c)}%` }} />
    </div>
  </div>
);

export const AllClear = () => (
  <div className="glass-card p-8 text-center" data-testid="campaign-all-clear">
    <CheckCircle2 className="h-8 w-8 text-green-400 mx-auto mb-3" />
    <p className="text-white font-medium">All emails reviewed</p>
    <p className="text-xs text-gray-500 mt-1">All current cadence emails have been sent or skipped.</p>
  </div>
);
